/**
 * VFX Nodes — Redprint nodes for controlling VFX systems.
 *
 * These nodes allow visual scripts to:
 * - Play/Stop VFX effects
 * - Trigger particle bursts
 * - Set exposed VFX variables (parameters)
 * - Query VFX state
 */
import type { NodeBehaviour } from '../types';
import { input, output, flowIn, flowOut } from '../node/NodeRegistry';

// ============================================================================
// VFX CONTROL
// ============================================================================

/**
 * Play VFX - Start playing a VFX effect on an entity.
 */
export const PlayVFX: NodeBehaviour = {
  id: 'vfx.play',
  name: 'Play VFX',
  category: 'VFX',
  color: '#E91E63',
  icon: 'Sparkles',
  description: 'Start playing a VFX effect',
  keywords: ['particle', 'effect', 'spawn', 'start'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('effectId', 'Effect ID', 'string', ''),
    input('resetOnPlay', 'Reset', 'bool', true),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    // Runtime: engine bridge handles this
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const effectId = ctx.resolveInput('effectId');
    const reset = ctx.resolveInput('resetOnPlay');
    //ctx.emitStatement(`engine_vfx_play(${entity}, ${effectId}, ${reset})`);
    throw new Error('PlayVFX is not implemented yet');
    return undefined;
  },
};

/**
 * Stop VFX - Stop a VFX effect on an entity.
 */
export const StopVFX: NodeBehaviour = {
  id: 'vfx.stop',
  name: 'Stop VFX',
  category: 'VFX',
  color: '#E91E63',
  icon: 'SquareX',
  description: 'Stop playing a VFX effect',
  keywords: ['particle', 'effect', 'stop', 'end', 'kill'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('immediate', 'Immediate', 'bool', false),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const immediate = ctx.resolveInput('immediate');
    //ctx.emitStatement(`engine_vfx_stop(${entity}, ${immediate})`);
    throw new Error('StopVFX is not implemented yet');
    return undefined;
  },
};

/**
 * Restart VFX - Reset and restart a VFX effect.
 */
export const RestartVFX: NodeBehaviour = {
  id: 'vfx.restart',
  name: 'Restart VFX',
  category: 'VFX',
  color: '#E91E63',
  icon: 'RotateCcw',
  description: 'Reset and restart a VFX effect',
  keywords: ['particle', 'effect', 'reset', 'replay'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    //ctx.emitStatement(`engine_vfx_restart(${entity})`);
    throw new Error('RestartVFX is not implemented yet');
    return undefined;
  },
};

// ============================================================================
// VFX SPAWNING
// ============================================================================

/**
 * Spawn VFX Burst - Trigger immediate particle burst.
 */
export const SpawnBurst: NodeBehaviour = {
  id: 'vfx.spawn_burst',
  name: 'Spawn Burst',
  category: 'VFX',
  color: '#E91E63',
  icon: 'Zap',
  description: 'Trigger an immediate particle burst',
  keywords: ['particle', 'burst', 'emit', 'spawn'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('count', 'Count', 'i32', 10),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const count = ctx.resolveInput('count');
    //ctx.emitStatement(`engine_vfx_spawn_burst(${entity}, ${count})`);
    throw new Error('SpawnBurst is not implemented yet');
    return undefined;
  },
};

/**
 * Set VFX Position - Set the emitter position.
 */
export const SetVFXPosition: NodeBehaviour = {
  id: 'vfx.set_position',
  name: 'Set VFX Position',
  category: 'VFX',
  color: '#E91E63',
  icon: 'MapPin',
  description: 'Set the VFX emitter position',
  keywords: ['particle', 'position', 'location', 'move'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const x = ctx.resolveInput('x');
    const y = ctx.resolveInput('y');
    //ctx.emitStatement(`engine_vfx_set_position(${entity}, ${x}, ${y})`);
    throw new Error('SetVFXPosition is not implemented yet');
    return undefined;
  },
};

// ============================================================================
// VFX VARIABLES - SETTERS
// ============================================================================

/**
 * Set VFX Float - Set a float variable on a VFX effect.
 */
export const SetVFXFloat: NodeBehaviour = {
  id: 'vfx.set_float',
  name: 'Set VFX Float',
  category: 'VFX',
  color: '#9C27B0',
  icon: 'Variable',
  description: 'Set a float variable on a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'float', 'number'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
    input('value', 'Value', 'f32', 0),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    const value = ctx.resolveInput('value');
    //ctx.emitStatement(`engine_vfx_set_float(${entity}, ${index}, ${value})`);
    throw new Error('SetVFXFloat is not implemented yet');
    return undefined;
  },
};

/**
 * Set VFX Vec2 - Set a vec2 variable on a VFX effect.
 */
export const SetVFXVec2: NodeBehaviour = {
  id: 'vfx.set_vec2',
  name: 'Set VFX Vec2',
  category: 'VFX',
  color: '#9C27B0',
  icon: 'Variable',
  description: 'Set a vec2 variable on a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'vector', 'vec2'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    const x = ctx.resolveInput('x');
    const y = ctx.resolveInput('y');
    //ctx.emitStatement(`engine_vfx_set_vec2(${entity}, ${index}, ${x}, ${y})`);
    throw new Error('SetVFXVec2 is not implemented yet');
    return undefined;
  },
};

/**
 * Set VFX Vec3 - Set a vec3 variable on a VFX effect.
 */
export const SetVFXVec3: NodeBehaviour = {
  id: 'vfx.set_vec3',
  name: 'Set VFX Vec3',
  category: 'VFX',
  color: '#9C27B0',
  icon: 'Variable',
  description: 'Set a vec3 variable on a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'vector', 'vec3'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('z', 'Z', 'f32', 0),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    const x = ctx.resolveInput('x');
    const y = ctx.resolveInput('y');
    const z = ctx.resolveInput('z');
    //ctx.emitStatement(`engine_vfx_set_vec3(${entity}, ${index}, ${x}, ${y}, ${z})`);
    throw new Error('SetVFXVec3 is not implemented yet');
    return undefined;
  },
};

/**
 * Set VFX Color - Set a color variable on a VFX effect.
 */
export const SetVFXColor: NodeBehaviour = {
  id: 'vfx.set_color',
  name: 'Set VFX Color',
  category: 'VFX',
  color: '#9C27B0',
  icon: 'Palette',
  description: 'Set a color variable on a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'color', 'tint'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
    input('color', 'Color', 'color', 0xFFFFFFFF),
  ],
  outputs: [flowOut()],
  execute(ctx) {
    ctx.triggerFlow('exec_out');
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    const color = ctx.resolveInput('color');
    //ctx.emitStatement(`engine_vfx_set_color(${entity}, ${index}, ${color})`);
    throw new Error('SetVFXColor is not implemented yet');
    return undefined;
  },
};

// ============================================================================
// VFX VARIABLES - GETTERS (Pure nodes)
// ============================================================================

/**
 * Get VFX Float - Get a float variable from a VFX effect.
 */
export const GetVFXFloat: NodeBehaviour = {
  id: 'vfx.get_float',
  name: 'Get VFX Float',
  category: 'VFX',
  isPure: true,
  color: '#9C27B0',
  icon: 'Variable',
  description: 'Get a float variable from a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'float', 'read'],
  inputs: [
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
  ],
  outputs: [output('value', 'Value', 'f32')],
  execute(ctx) {
    // Runtime would query the VFX system
    ctx.setOutput('value', 0);
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    return ctx.emit('engine_vfx_get_float', [entity, index], 'f32');
  },
};

/**
 * Get VFX Vec2 - Get a vec2 variable from a VFX effect.
 */
export const GetVFXVec2: NodeBehaviour = {
  id: 'vfx.get_vec2',
  name: 'Get VFX Vec2',
  category: 'VFX',
  isPure: true,
  color: '#9C27B0',
  icon: 'Variable',
  description: 'Get a vec2 variable from a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'vector', 'read'],
  inputs: [
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
  ],
  outputs: [output('value', 'Value', 'vec2')],
  execute(ctx) {
    ctx.setOutput('value', { x: 0, y: 0 });
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    return ctx.emit('engine_vfx_get_vec2', [entity, index], 'vec2');
  },
};

/**
 * Get VFX Color - Get a color variable from a VFX effect.
 */
export const GetVFXColor: NodeBehaviour = {
  id: 'vfx.get_color',
  name: 'Get VFX Color',
  category: 'VFX',
  isPure: true,
  color: '#9C27B0',
  icon: 'Palette',
  description: 'Get a color variable from a VFX effect',
  keywords: ['particle', 'variable', 'parameter', 'color', 'read'],
  inputs: [
    input('entity', 'Entity', 'entity'),
    input('variableIndex', 'Variable Index', 'i32', 0),
  ],
  outputs: [output('value', 'Value', 'color')],
  execute(ctx) {
    ctx.setOutput('value', 0xFFFFFFFF);
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    const index = ctx.resolveInput('variableIndex');
    return ctx.emit('engine_vfx_get_color', [entity, index], 'color');
  },
};

// ============================================================================
// VFX QUERIES (Pure nodes)
// ============================================================================

/**
 * Is VFX Playing - Check if a VFX effect is currently playing.
 */
export const IsVFXPlaying: NodeBehaviour = {
  id: 'vfx.is_playing',
  name: 'Is VFX Playing',
  category: 'VFX',
  isPure: true,
  color: '#E91E63',
  icon: 'CirclePlay',
  description: 'Check if a VFX effect is currently playing',
  keywords: ['particle', 'effect', 'playing', 'active', 'check'],
  inputs: [input('entity', 'Entity', 'entity')],
  outputs: [output('isPlaying', 'Is Playing', 'bool')],
  execute(ctx) {
    ctx.setOutput('isPlaying', false);
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    return ctx.emit('engine_vfx_is_playing', [entity], 'bool');
  },
};

/**
 * Get VFX Particle Count - Get the current alive particle count.
 */
export const GetVFXParticleCount: NodeBehaviour = {
  id: 'vfx.get_particle_count',
  name: 'Get Particle Count',
  category: 'VFX',
  isPure: true,
  color: '#E91E63',
  icon: 'Hash',
  description: 'Get the current alive particle count',
  keywords: ['particle', 'count', 'number', 'alive'],
  inputs: [input('entity', 'Entity', 'entity')],
  outputs: [output('count', 'Count', 'i32')],
  execute(ctx) {
    ctx.setOutput('count', 0);
  },
  compile(ctx) {
    const entity = ctx.resolveInput('entity');
    return ctx.emit('engine_vfx_get_particle_count', [entity], 'i32');
  },
};

// ============================================================================
// EXPORT
// ============================================================================

/**
 * All VFX nodes.
 */
export const VFX_NODES: readonly NodeBehaviour[] = [
  // Control
  PlayVFX,
  StopVFX,
  RestartVFX,
  // Spawning
  SpawnBurst,
  SetVFXPosition,
  // Variable Setters
  SetVFXFloat,
  SetVFXVec2,
  SetVFXVec3,
  SetVFXColor,
  // Variable Getters
  GetVFXFloat,
  GetVFXVec2,
  GetVFXColor,
  // Queries
  IsVFXPlaying,
  GetVFXParticleCount,
];
